import { z } from 'zod';

/**
 * Foutformaat RFC 9457 (application/problem+json).
 * `code` is de machineleesbare sleutel, bv. POLICY_DENIED of FEATURE_DISABLED.
 */
export const problemSchema = z.object({
  type: z.string(),
  title: z.string(),
  status: z.number().int(),
  detail: z.string().optional(),
  instance: z.string().optional(),
  code: z.string(),
  requestId: z.string().optional(),
  errors: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
});
export type Problem = z.infer<typeof problemSchema>;

export const problemCodes = [
  'VALIDATION_FAILED',
  'UNAUTHENTICATED',
  'MFA_REQUIRED',
  'POLICY_DENIED',
  'FEATURE_DISABLED',
  'TENANT_SUSPENDED',
  'NOT_FOUND',
  'CONFLICT',
  'LAST_ADMIN',
  // Een bericht dat al bevestigd is, wijzigt niet meer (Stap 8). Sinds ADR-0070 een terugval:
  // normaal weigert `LOCKED_REQUIRES_ACK` al eerder.
  'ALREADY_ACKNOWLEDGED',
  // Een bericht met leesbevestiging ligt na publicatie vast (Extra 3, ADR-0070). Een eigen code,
  // want het scherm moet hier de regel uitleggen en naar "Corrigeren" wijzen - niet "er ging iets
  // mis" tonen.
  'LOCKED_REQUIRES_ACK',
  // De handeling past niet bij de toestand: een gepubliceerd bericht verwijderen, een concept
  // intrekken. Ook een eigen code, om dezelfde reden.
  'WRONG_STATUS',
  // Namen van medewerkers zijn niet zichtbaar (Stap 10, ADR-0014).
  'NAMES_NOT_VISIBLE',
  // Het meegestuurde tijdstip van een toestel is niet bruikbaar (Stap 12, ADR-0047): ouder dan
  // zeven dagen, of een klok die meer dan een dag vooruit staat.
  'CLIENT_TIME_REJECTED',
  // Geweigerd, maar niet omdat een recht ontbreekt (Stap 37): de verkeerde persoon voor dit ene
  // geval. Wie geen behandelaar is van dit formulier, mist geen recht dat zijn beheerder kan geven.
  'FORBIDDEN',
  // Deze persoon heeft al een account bij dit bedrijf (Extra 4); uitnodigen is dan zinloos.
  'ALREADY_MEMBER',
  // Er staat al een uitnodiging open voor dit adres (Extra 4); opnieuw versturen kan wel.
  'ALREADY_INVITED',
  // De uitnodiging is ingetrokken (Extra 4). `410` en geen `404`: wie hier komt, kreeg de link
  // echt - hij hoort te weten dat ze ongeldig gemaakt is en niet dat hij zich vergist heeft.
  'GONE',
  // Deze persoon kreeg in de afgelopen 24 uur al een herinnering voor dit bericht (Stap 17). Met
  // het tijdstip erbij in `errors`, want de knop toont daarna "Herinnerd om 08:12".
  'REMINDER_TOO_SOON',
  // Zelfregistratie op uitnodiging (Stap 34, REG-1): het scherm vertaalt elke code naar een eigen
  // foutscherm (spec frame 6). `EXPIRED` draagt `validUntil` in `errors`, `RATE_LIMITED` daar
  // `retryAfter`.
  'EXPIRED',
  'REVOKED',
  'FULL',
  'UNKNOWN',
  'EMPLOYEE_NUMBER_TAKEN',
  // Registratie op een bestaand personeelsnummer: HR bevestigt eerst (SEC-5).
  'AWAITING_APPROVAL',
  'WEAK_PIN',
  // De kiosk (Stap 40, KIO-1). `INVALID_CREDENTIALS` zegt nooit welk van de twee fout was;
  // `LOCKED` draagt `retryAfter` in `errors`.
  'INVALID_CREDENTIALS',
  'LOCKED',
  'NOT_ACTIVATED',
  'DEVICE_REVOKED',
  'RATE_LIMITED',
  // De AI-dienst van de schrijfhulp antwoordde niet of weigerde (ADR-0102).
  'AI_UNAVAILABLE',
  'INTERNAL_ERROR',
] as const;
export type ProblemCode = (typeof problemCodes)[number];
